import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import axios from "../scripts/axiosConfig";
import ContestCard from "../components/ContestCard";
import Footer from '../components/Footer';

import { MdPerson } from "react-icons/md";
import { CiCirclePlus } from "react-icons/ci";
import { IoMdSearch } from "react-icons/io";


import "../styles/Contests.scss";
import { getCustomClaim } from "../scripts/Misc";

type ContestData = {
    publicContests: ContestType[] | null;
    userContests: ContestType[] | [];
}

type ContestType = {
    contestSlug: string;
    name: string;
    description?: string;

    // Timing
    openContestImmediately: boolean;
    startTime: string;
    openContestForever: boolean;
    endTime?: string | null;
    timedContest: boolean;
    duration?: number | null;

    // Questions
    problems: string[];
    maxQuestions: number;
    randomizeQuestions: boolean;

    // Security
    enableProctored: boolean;
    maxWarnings?: number;
    passwordProtected: boolean;
    password?: string;
    emailRestriction: "all" | "domain" | "emails";
    public: boolean;

    // Customization
    orgName?: string;
    customLogo?: string;
    customBanner?: string;
    eventRules: string;
    eventPrizes: string;
    eventFAQ: string;
    eventTerms: string;
    eventPrivacy: string;
    tags?: string[];

    // Timestamps
    createdAt: Date;
    updatedAt: Date;
};

export default function Contests() {

    const { userJson, currentUser } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [enableContestsQuery, setEnableContestsQuery] = useState(false);

    const debouncedOnChange = useDebouncedCallback((e) => {
        console.log("Debounced search query: ", e);
        setSearchQuery(e.target.value);
    }, 500);

    const { data: allContestsData, isError, error, isLoading, isPending, isPaused } = useQuery<ContestData>({
        queryKey: ['allContests'],
        queryFn: async () => {
            let body;
            if (currentUser && currentUser !== "none") {
                body = {
                    creator: await getCustomClaim(currentUser, "mongoID"),
                }
            }
            else {
                body = {
                    creator: null,
                }
            }

            const response = await axios.post("/contests/get-all", body, {
                headers: {
                    "Content-Type": "application/json"
                },
            });
            return response.data;
            
        },
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        enabled: enableContestsQuery,
    });

    useEffect(() => {
        if (currentUser || currentUser == "none") {
            console.log("firing contests query");
            setEnableContestsQuery(true);
        }
    }, [currentUser]);


    const filteredContests = allContestsData?.publicContests?.filter(contest =>
        contest.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <div className="Contests">
                <div className="header">
                    <div className="left">
                        <div className="pageTitle">Contests</div>
                        <div 
                            className="subTitle"
                            onClick={async () => {
                                console.log(await getCustomClaim(currentUser, "_id"));
                            }}
                        >
                            View contests hosted on c0dify.
                        </div>
                    </div>
                    <div className="right">
                        {
                            (!isLoading && !isError && allContestsData?.userContests && allContestsData?.userContests.length > 0) && (
                                <button
                                    onClick={() => navigate('/contests/mine')}
                                    disabled={isLoading}
                                    data-tooltip-id="tt" data-tooltip-content={`You have ${allContestsData?.userContests.length} contests`}
                                >
                                    <MdPerson />
                                    Your contests
                                    <span className="count">
                                        ({allContestsData?.userContests.length})
                                    </span>
                                </button>
                            )
                        }
                        
                        <button
                            onClick={() => navigate('/contests/create')}
                            disabled={isLoading || isPending}
                        >
                            <CiCirclePlus />
                            Create Contest
                        </button>
                    </div>
                </div>

                <div className="publicContests">

                    <div className="searchbarContainer">
                        <div className="searchbar">
                            <div className="searchIcon">
                                <IoMdSearch />
                            </div>
                            <input
                                type="text"
                                placeholder="Search contests..."
                                onChange={debouncedOnChange}
                                disabled={isLoading || isPending || isError}
                            />
                        </div>
                    </div>

                    <div className="contestsGrid">

                        {
                            (isLoading || isPending) && (
                                <>
                                    {
                                        (new Array(3).fill(0).map((_, index) => {
                                            return (
                                                <ContestCard
                                                    key={index}
                                                    name="Test Title 99"
                                                    description="test description blabla who let the dog out woof woof"
                                                    startTime={"99999999999"}
                                                    endTime={"99999999999"}
                                                    timedContest={true}
                                                    enableProctored={true}
                                                    duration={69}
                                                    slug={"skeleton-loading-not-real"}
                                                    skeleton={true}
                                                />
                                            )
                                        }))
                                    }
                                </>
                            )
                        }
                        
                        {
                            (filteredContests && filteredContests.length > 0) ? filteredContests.reverse().map((contest, index) => (
                                <ContestCard
                                    key={index}
                                    name={contest.name}
                                    description={contest.description || ""}
                                    startTime={contest.startTime}
                                    endTime={contest.endTime}
                                    timedContest={contest.timedContest}
                                    slug={contest.contestSlug}
                                    enableProctored={contest.enableProctored}
                                    duration={contest.duration}
                                />
                            )) : (
                                (!isLoading && !isPending) && (<p>No contests available</p>) 
                            )
                        }
                    </div>
                    
                </div>

            </div>
            <Footer />

        </>
    )
}